import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getHouderDetail, wijzigHouder, verwijderHouder, getGebieden } from '../api/api';
import VerversPill from '../components/VerversPill';
import { usePresence, PresenceLabel } from '../components/usePresence';
import { useAuth } from '../components/AuthContext';
import AdresAutocomplete from '../components/AdresAutocomplete';
import OpmerkingenVeld from '../components/OpmerkingenVeld';
import { updateHouderOpmerkingen } from '../api/api';
import { fmtId, fmtDatumNL } from '../util/format';
import Klokje from '../components/Klokje';
import InrichtingToevoegenModal from '../components/InrichtingToevoegenModal';
import KaartMetLegenda from '../components/KaartMetLegenda';

function Field({ label, value, editing, onChange, type = 'text' }) {
  if (!editing) {
    return (
      <div className="field">
        <span className="field-label">{label}</span>
        <span className={`field-value ${!value ? 'empty' : ''}`}>
          {value ? (type === 'date' ? fmtDatumNL(value) : value) : '—'}
        </span>
      </div>
    );
  }
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      {type === 'textarea' ? (
        <textarea rows={3} value={value || ''} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

export default function HouderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [toevoegenOpen, setToevoegenOpen] = useState(false);
  const [form, setForm] = useState({});
  const [error, setError] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [gebieden, setGebieden] = useState([]);

  const laad = useCallback(async () => {
    try {
      const [res, alleGeb] = await Promise.all([
        getHouderDetail(id),
        getGebieden(undefined, true),
      ]);
      setData(res.data);
      setForm(res.data.houder);
      setGebieden(alleGeb.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  }, [id]);

  useEffect(() => { laad(); }, [laad]);

  // Lichte refresh: alleen het houderdetail opnieuw ophalen (niet de zware
  // gebieden-overlay, die is bij openen geladen en verandert zelden).
  const lichteRefresh = useCallback(async () => {
    const res = await getHouderDetail(id);
    setData(res.data);
    setForm(res.data.houder);
  }, [id]);

  const { gebruiker } = useAuth();
  const presenceAnderen = usePresence('houder', id, editing, gebruiker?.naam);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const opslaan = async () => {
    setBezig(true);
    setError(null);
    try {
      const payload = { ...form };
      delete payload.houder_id;
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      payload.verwachte_gewijzigd_op = form.gewijzigd_op || null;
      delete payload.gewijzigd_op;
      delete payload.aangemaakt_op;
      await wijzigHouder(id, payload);
      setEditing(false);
      laad();
    } catch (e) {
      if (e.response?.status === 409) {
        setEditing(false);
        setError('Een collega heeft deze houder zojuist gewijzigd. De pagina is opnieuw geladen met de actuele gegevens.');
        lichteRefresh();
      } else {
        setError(e.response?.data?.detail || e.message);
      }
    } finally {
      setBezig(false);
    }
  };

  const verwijderen = async () => {
    if (!window.confirm('Weet je zeker dat je deze houder wilt verwijderen?')) return;
    try {
      await verwijderHouder(id);
      navigate('/beregening/houders');
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  if (!data) return <div className="empty-state"><span className="spinner" /></div>;

  const { houder, inrichtingen } = data;
  const naam = houder.type === 'RP'
    ? houder.naam
    : [houder.voornaam, houder.tussenvoegsel, houder.achternaam].filter(Boolean).join(' ');

  return (
    <div className="main-inner">
      <div className="detail-layout">
        <div className="detail-header page-header">
        <div>
          <h1 className="page-title">{naam || fmtId(houder.houder_id)}</h1>
          <p className="page-subtitle">
            <span className="mono">{fmtId(houder.houder_id)}</span> · {houder.type === 'RP' ? 'Bedrijf' : 'Persoon'}
            {houder.kvk_nummer && ` · KvK ${houder.kvk_nummer}`}
          </p>
        </div>
        <div className="row">
          <PresenceLabel anderen={presenceAnderen} />
          {!editing && <VerversPill onVervers={lichteRefresh} />}
          {!editing ? (
            <>
              {inrichtingen.length === 0 && (
                <button className="btn btn-danger" onClick={verwijderen}>Verwijderen</button>
              )}
              <button className="btn btn-secondary" onClick={() => setToevoegenOpen(true)}>
                + Inrichting toevoegen
              </button>
              <button className="btn btn-primary" onClick={() => setEditing(true)}>Bewerken</button>
            </>
          ) : (
            <>
              <button className="btn btn-ghost" onClick={() => { setEditing(false); setForm(houder); }}>Annuleren</button>
              <button className="btn btn-primary" onClick={opslaan} disabled={bezig}>
                {bezig ? <><span className="spinner" /> Opslaan...</> : 'Opslaan'}
              </button>
            </>
          )}
        </div>
        </div>

        <div className="detail-main">

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="card-header"><h3 className="card-title">Identificatie</h3></div>
        <div className="field-grid">
          {houder.type === 'RP' ? (
            <>
              <Field label="Bedrijfsnaam" value={form.naam} editing={editing} onChange={(v) => update('naam', v)} />
              <Field label="KvK-nummer" value={form.kvk_nummer} editing={editing} onChange={(v) => update('kvk_nummer', v)} />
            </>
          ) : (
            <>
              <Field label="Voornaam" value={form.voornaam} editing={editing} onChange={(v) => update('voornaam', v)} />
              <Field label="Tussenvoegsel" value={form.tussenvoegsel} editing={editing} onChange={(v) => update('tussenvoegsel', v)} />
              <Field label="Achternaam" value={form.achternaam} editing={editing} onChange={(v) => update('achternaam', v)} />
              <Field label="Geboortedatum" value={form.geboortedatum} editing={editing} onChange={(v) => update('geboortedatum', v)} type="date" />
            </>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Adres</h3>
          {editing && (
            <>
              <span style={{ flex: 1 }} />
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  const naarBelgie = form.land !== 'België';
                  setForm({
                    ...form,
                    land: naarBelgie ? 'België' : 'Nederland',
                    adres: '', postcode: '', plaats: '',
                  });
                }}
              >
                {form.land === 'België' ? '← Adres in Nederland' : 'Adres in België?'}
              </button>
            </>
          )}
        </div>
        <div className="field-grid cols-3">
          {editing && form.land === 'Nederland' ? (
            <div className="field">
              <span className="field-label">Adres</span>
              <AdresAutocomplete
                value={form.adres || ''}
                onChange={(v) => update('adres', v)}
                onSelected={({ adres, postcode, plaats }) => {
                  setForm({ ...form, adres, postcode, plaats });
                }}
              />
            </div>
          ) : (
            <Field label="Adres" value={form.adres} editing={editing} onChange={(v) => update('adres', v)} />
          )}
          <Field label="Postcode" value={form.postcode} editing={editing} onChange={(v) => update('postcode', v)} />
          <Field label="Plaats" value={form.plaats} editing={editing} onChange={(v) => update('plaats', v)} />
        </div>
        {editing && form.land === 'België' && (
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '8px 0 0 0' }}>
            Belgisch adres — handmatige invoer
          </p>
        )}
        {!editing && form.land && form.land !== 'Nederland' && (
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '8px 0 0 0' }}>
            Land: {form.land}
          </p>
        )}
      </div>

      <div className="card">
        <div className="card-header"><h3 className="card-title">Contact</h3></div>
        <div className="field-grid">
          <Field label="E-mailadres" value={form.emailadres} editing={editing} onChange={(v) => update('emailadres', v)} type="email" />
          <Field label="Telefoonnummer" value={form.telefoonnummer} editing={editing} onChange={(v) => update('telefoonnummer', v)} />
        </div>
      </div>

      {(() => {
        const allePutten = inrichtingen.flatMap(i => i.putten || []);
        const dedupe = Array.from(new Map(allePutten.map(p => [p.put_id, p])).values());
        if (dedupe.length === 0) return null;
        return (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Locatie</h3>
              <span className="chip">{dedupe.length} {dedupe.length === 1 ? 'put' : 'putten'}</span>
            </div>
            <KaartMetLegenda
              punten={dedupe.map(p => ({
                put_id: p.put_id,
                x_rd: p.x_rd,
                y_rd: p.y_rd,
                perceel: p.perceel,
                onClick: () => navigate(`/beregening/putten/${p.put_id}`),
              }))}
              overlays={gebieden}
              hoogte={340}
            />
          </div>
        );
      })()}

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Inrichtingen</h3>
          <span className="chip">{inrichtingen.length}</span>
        </div>
        {inrichtingen.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 24 }}>
            Geen inrichtingen.
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Verzoeknummer</th>
                <th>Type</th>
                <th>Ingangsdatum</th>
                <th>Doel</th>
                <th>Putten</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {inrichtingen.map(i => (
                <tr
                  key={i.inrichting_id}
                  className="clickable"
                  onClick={() => navigate(`/beregening/inrichtingen/${i.inrichting_id}`)}
                  style={i.einddatum ? { opacity: 0.55 } : undefined}
                >
                  <td><span className="mono">{fmtId(i.inrichting_id)}</span></td>
                  <td>{i.verzoeknummer || '—'}</td>
                  <td>
                    <span className={`chip ${i.rechtstype === 'vergunning' ? 'chip-warning' : ''}`}>
                      {i.rechtstype}
                    </span>
                  </td>
                  <td>{i.ingangsdatum ? fmtDatumNL(i.ingangsdatum) : '—'}</td>
                  <td>{i.doel || '—'}</td>
                  <td>{i.putten?.length || 0}</td>
                  <td>
                    {(() => {
                      /* Drie toestanden, niet twee. Een inrichting waarvan het
                         gebruiksrecht pas volgende maand ingaat is niet actief;
                         een inrichting die wel loopt maar waar een wijziging
                         voor klaarstaat is dat wel, maar niet ongewijzigd. */
                      if (i.einddatum) {
                        return (
                          <span className="chip chip-danger">
                            Beëindigd {fmtDatumNL(i.einddatum)}
                          </span>
                        );
                      }
                      const nogNiet = i.inwerkingtreding
                        && i.inwerkingtreding > new Date().toISOString().slice(0, 10);
                      if (nogNiet) {
                        const t = `Dit gebruiksrecht gaat pas in op ${fmtDatumNL(i.inwerkingtreding)}`;
                        return (
                          <span className="chip chip-toekomstig" title={t}>
                            <Klokje grootte={12} titel={t} />Vanaf{' '}
                            <span className="chip-datum">{fmtDatumNL(i.inwerkingtreding)}</span>
                          </span>
                        );
                      }
                      if (i.klaarstaand_vanaf) {
                        const t = `Er staat een wijziging klaar die ingaat op ${fmtDatumNL(i.klaarstaand_vanaf)}`;
                        return (
                          <span className="chip chip-toekomstig" title={t}>
                            <Klokje grootte={12} titel={t} />Actief
                          </span>
                        );
                      }
                      return <span className="chip chip-success">Actief</span>;
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
        </div>

        <InrichtingToevoegenModal
          isOpen={toevoegenOpen}
          onClose={() => setToevoegenOpen(false)}
          houderId={id}
          houderNaam={houder?.naam || [houder?.voornaam, houder?.tussenvoegsel, houder?.achternaam].filter(Boolean).join(' ')}
          inrichtingen={inrichtingen}
        />

        <aside className="detail-aside">
          <OpmerkingenVeld
            initialValue={form.opmerkingen}
            onSave={async (tekst) => {
              await updateHouderOpmerkingen(id, tekst || null);
              update('opmerkingen', tekst);
            }}
          />
        </aside>
      </div>
    </div>
  );
}
